// ** Modules
import { useQuery } from '@apollo/client/react';
import { gql, type TypedDocumentNode } from '@apollo/client';

// ** Utils
import { RecursivePageLink } from './fragments';

// ** Types
import type { Query } from '@/types/graphql';

// ─── Query ───────────────────────────────────────────────────────────────────
//
// Targets the `mega-menu` single type (Strapi Variant 2 — forked
// `mega-menu.column` / `mega-menu.link` components, reused `footer.contact`
// and `footer.social-link` shared components).
//
export const getMegaMenu: TypedDocumentNode<Pick<Query, 'megaMenu'>, { locale?: string | null }> = gql`
    ${RecursivePageLink}

    fragment MegaMenuLinkFields on ComponentMegaMenuLink {
        id
        label
        url
        active
        authenticated
        onlyVisibleToGroups
        ssoGroups
        pageLink {
            ...RecursivePageLink
        }
    }

    query MegaMenu($locale: I18NLocaleCode) {
        megaMenu(locale: $locale) {
            showMemberLoginButton
            memberLinks {
                ...MegaMenuLinkFields
            }
            columns {
                id
                title
                active
                items {
                    ...MegaMenuLinkFields
                }
            }
            contact {
                id
                heading
                phone
                email
                addressLines {
                    id
                    content
                }
            }
            socialLinks(pagination: { page: 1, pageSize: 1000 }) {
                id
                key
                href
            }
            faqPageLink {
                ...RecursivePageLink
            }
            faqUrl
        }
    }
`;

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Fetch the mega-menu body for a given locale.
 */
export const useGetMegaMenu = (locale: string, forceRefetch: boolean) => {
	const props = useQuery(getMegaMenu, {
		variables: { locale },
		skip: !locale,
		fetchPolicy: forceRefetch ? 'no-cache' : 'cache-first'
	});

	const data = props.data ?? { megaMenu: null };

	return { ...props, data };
};
